import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Search, Plus, Edit, Trash2, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TiposDocumentosPage = () => {
  const { toast } = useToast();
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    ativo: true
  });

  useEffect(() => {
    carregarTiposDocumentos();
  }, [filtroNome, filtroStatus]);

  const carregarTiposDocumentos = async () => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filtroStatus === 'ativos') params.append('ativos', 'true');
      else if (filtroStatus === 'inativos') params.append('ativos', 'false');
      if (filtroNome.trim()) params.append('nome', filtroNome.trim());

      const response = await axios.get(`${API}/api/tipos-documentos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiposDocumentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de documentos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os tipos de documentos",
        variant: "destructive"
      });
    } finally {
      setCarregando(false);
    }
  };

  const handleSalvar = async () => {
    if (!form.nome.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Preencha o nome do tipo de documento",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editando) {
        await axios.put(
          `${API}/api/tipos-documentos/${editando.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Atualizado!",
          description: "Tipo de documento atualizado com sucesso"
        });
      } else {
        await axios.post(
          `${API}/api/tipos-documentos`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Criado!",
          description: "Tipo de documento criado com sucesso"
        });
      }

      setShowModal(false);
      setForm({ nome: '', descricao: '', ativo: true });
      setEditando(null);
      carregarTiposDocumentos();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível salvar",
        variant: "destructive"
      });
    }
  };

  const handleEditar = (tipo) => {
    setEditando(tipo);
    setForm({
      nome: tipo.nome,
      descricao: tipo.descricao || '',
      ativo: tipo.ativo
    });
    setShowModal(true);
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Deseja realmente desativar este tipo de documento?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/tipos-documentos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Desativado!",
        description: "Tipo de documento desativado com sucesso"
      });
      
      carregarTiposDocumentos();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível desativar",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Tipos de Documentos
              </CardTitle>
              <CardDescription className="text-orange-100">
                Gerencie os tipos de documentos que podem ser enviados
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setEditando(null);
                setForm({ nome: '', descricao: '', ativo: true });
                setShowModal(true);
              }}
              className="bg-white text-orange-600 hover:bg-orange-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Tipo
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome..."
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ativos">Ativos</SelectItem>
                  <SelectItem value="inativos">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {carregando ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando...</p>
            </div>
          ) : tiposDocumentos.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 text-lg mb-2">Nenhum tipo cadastrado</p>
              <p className="text-gray-500 text-sm">Clique em "Novo Tipo" para começar</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {tiposDocumentos.map((tipo) => (
                <Card key={tipo.id} className="border border-gray-200">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {tipo.nome}
                        </h3>
                        {tipo.descricao && (
                          <p className="text-sm text-gray-600 mb-2">{tipo.descricao}</p>
                        )}
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            tipo.ativo 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {tipo.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                          <span className="text-xs text-gray-500">
                            Criado em {new Date(tipo.criado_em).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditar(tipo)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletar(tipo.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="bg-orange-600 text-white p-4 rounded-t-lg">
              <h3 className="text-xl font-bold">
                {editando ? 'Editar Tipo de Documento' : 'Novo Tipo de Documento'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={form.nome}
                  onChange={(e) => setForm({...form, nome: e.target.value})}
                  placeholder="Ex: Atestado Médico"
                />
              </div>

              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={form.descricao}
                  onChange={(e) => setForm({...form, descricao: e.target.value})}
                  placeholder="Descrição opcional"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={form.ativo}
                  onChange={(e) => setForm({...form, ativo: e.target.checked})}
                  className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                />
                <Label htmlFor="ativo">Ativo</Label>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-b-lg flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setEditando(null);
                  setForm({ nome: '', descricao: '', ativo: true });
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSalvar}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {editando ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiposDocumentosPage;
